import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type {
  Availability,
  BalancePaymentLink,
  CancelPayload,
  CharterAddon,
  CharterBooking,
  CharterOrder,
  CharterPackage,
  CharterProvider,
  CharterService,
  CreateAddonPayload,
  CreatePackagePayload,
  CreatePortfolioWorkPayload,
  CreateServicePayload,
  IntakeForm,
  OverrideDurationPayload,
  OverrideDurationResult,
  PaginationQuery,
  PortfolioWork,
  RefundDecisionPayload,
  ReschedulePayload,
  ScheduleSessionPayload,
  SetAvailabilityPayload,
  SetIntakeFormPayload,
  UpdateAddonPayload,
  UpdateBrandAssetsPayload,
  UpdateGatewayStatePayload,
  UpdatePackagePayload,
  UpdatePortfolioWorkPayload,
  UpdateProviderProfilePayload,
  UpdateServicePayload,
  UpdateWaitlistEntryPayload,
  WaitlistEntry,
} from '../types';
import type { CharterCancellationPolicy } from '../types';

/**
 * charter-api client for the Osprey back-office.
 *
 * Single-origin: the UI calls osprey-api, which proxies `/charter/*` to charter-api
 * (base path `/api/v1`). e.g. `/charter/me/services` → charter-api `/api/v1/me/services`.
 * ⚠️ The osprey-api `/charter` proxy does not exist yet — see the PR body; adding it
 * (mirroring the existing bookings proxy) is the required follow-up. No charter-api
 * base URL is hard-coded here, so the single-origin invariant (apiBase.ts) holds.
 *
 * Merchant routes (`/charter/me/*`) are tenant-scoped: `ApiFetch` sets `x-tenant-id`
 * from `selectedTenantId`, which charter-api's RequireMerchantContext reads. Admin
 * routes (`/charter/admin/*`) are cross-provider and take the provider id in the path.
 *
 * `ApiFetch.put` issues a PATCH on the wire (see shared/utils/fetch.ts); charter-api
 * updates are PATCH, so update calls use `.put`.
 */

const BASE = '/charter';

const qs = (query?: PaginationQuery): string => {
  if (!query) return '';
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.limit) params.set('limit', String(query.limit));
  if (query.search) params.set('search', query.search);
  const s = params.toString();
  return s ? `?${s}` : '';
};

// ── Merchant · Services ──────────────────────────────────────────────────────

export const servicesApi = {
  list: async (query?: PaginationQuery): Promise<CharterService[]> =>
    unwrap(await ApiFetch.get<Enveloped<CharterService[]>>(`${BASE}/me/services${qs(query)}`)),
  get: async (id: string): Promise<CharterService> =>
    unwrap(await ApiFetch.get<Enveloped<CharterService>>(`${BASE}/me/services/${id}`)),
  create: async (data: CreateServicePayload): Promise<CharterService> =>
    unwrap(await ApiFetch.post<Enveloped<CharterService>>(`${BASE}/me/services`, data)),
  update: async (id: string, data: UpdateServicePayload): Promise<CharterService> =>
    unwrap(await ApiFetch.put<Enveloped<CharterService>>(`${BASE}/me/services/${id}`, data)),
  remove: async (id: string): Promise<void> => {
    await ApiFetch.del(`${BASE}/me/services/${id}`);
  },
};

// ── Merchant · Packages ──────────────────────────────────────────────────────

export const packagesApi = {
  list: async (query?: PaginationQuery): Promise<CharterPackage[]> =>
    unwrap(await ApiFetch.get<Enveloped<CharterPackage[]>>(`${BASE}/me/packages${qs(query)}`)),
  create: async (data: CreatePackagePayload): Promise<CharterPackage> =>
    unwrap(await ApiFetch.post<Enveloped<CharterPackage>>(`${BASE}/me/packages`, data)),
  update: async (id: string, data: UpdatePackagePayload): Promise<CharterPackage> =>
    unwrap(await ApiFetch.put<Enveloped<CharterPackage>>(`${BASE}/me/packages/${id}`, data)),
  remove: async (id: string): Promise<void> => {
    await ApiFetch.del(`${BASE}/me/packages/${id}`);
  },
};

// ── Merchant · Add-ons ─────────────────────────────────────────────────────────

export const addonsApi = {
  list: async (query?: PaginationQuery): Promise<CharterAddon[]> =>
    unwrap(await ApiFetch.get<Enveloped<CharterAddon[]>>(`${BASE}/me/addons${qs(query)}`)),
  create: async (data: CreateAddonPayload): Promise<CharterAddon> =>
    unwrap(await ApiFetch.post<Enveloped<CharterAddon>>(`${BASE}/me/addons`, data)),
  update: async (id: string, data: UpdateAddonPayload): Promise<CharterAddon> =>
    unwrap(await ApiFetch.put<Enveloped<CharterAddon>>(`${BASE}/me/addons/${id}`, data)),
  remove: async (id: string): Promise<void> => {
    await ApiFetch.del(`${BASE}/me/addons/${id}`);
  },
};

// ── Merchant · Bookings ────────────────────────────────────────────────────────

export const bookingsApi = {
  list: async (query?: PaginationQuery): Promise<CharterBooking[]> =>
    unwrap(await ApiFetch.get<Enveloped<CharterBooking[]>>(`${BASE}/me/bookings${qs(query)}`)),
  get: async (id: string): Promise<CharterBooking> =>
    unwrap(await ApiFetch.get<Enveloped<CharterBooking>>(`${BASE}/me/bookings/${id}`)),
  confirm: async (id: string): Promise<CharterBooking> =>
    unwrap(await ApiFetch.post<Enveloped<CharterBooking>>(`${BASE}/me/bookings/${id}/confirm`)),
  cancel: async (id: string, data: CancelPayload): Promise<CharterBooking> =>
    unwrap(await ApiFetch.post<Enveloped<CharterBooking>>(`${BASE}/me/bookings/${id}/cancel`, data)),
  reschedule: async (id: string, data: ReschedulePayload): Promise<CharterBooking> =>
    unwrap(await ApiFetch.post<Enveloped<CharterBooking>>(`${BASE}/me/bookings/${id}/reschedule`, data)),
  overrideDuration: async (id: string, data: OverrideDurationPayload): Promise<OverrideDurationResult> =>
    unwrap(await ApiFetch.put<Enveloped<OverrideDurationResult>>(`${BASE}/me/bookings/${id}/duration`, data)),
  balanceLink: async (id: string): Promise<BalancePaymentLink> =>
    unwrap(await ApiFetch.post<Enveloped<BalancePaymentLink>>(`${BASE}/me/bookings/${id}/balance-payment-link`)),
};

// ── Merchant · Orders ──────────────────────────────────────────────────────────

export const ordersApi = {
  list: async (query?: PaginationQuery): Promise<CharterOrder[]> =>
    unwrap(await ApiFetch.get<Enveloped<CharterOrder[]>>(`${BASE}/me/orders${qs(query)}`)),
  get: async (id: string): Promise<CharterOrder> =>
    unwrap(await ApiFetch.get<Enveloped<CharterOrder>>(`${BASE}/me/orders/${id}`)),
  confirm: async (id: string): Promise<CharterOrder> =>
    unwrap(await ApiFetch.post<Enveloped<CharterOrder>>(`${BASE}/me/orders/${id}/confirm`)),
  cancelWithRefund: async (id: string, data: RefundDecisionPayload): Promise<CharterOrder> =>
    unwrap(await ApiFetch.post<Enveloped<CharterOrder>>(`${BASE}/me/orders/${id}/cancel`, data)),
  balanceLink: async (id: string): Promise<BalancePaymentLink> =>
    unwrap(await ApiFetch.post<Enveloped<BalancePaymentLink>>(`${BASE}/me/orders/${id}/balance-payment-link`)),
  scheduleSession: async (id: string, stepId: string, data: ScheduleSessionPayload): Promise<CharterOrder> =>
    unwrap(
      await ApiFetch.post<Enveloped<CharterOrder>>(
        `${BASE}/me/orders/${id}/sessions/${stepId}/schedule`,
        data,
      ),
    ),
};

// ── Merchant · Provider profile ────────────────────────────────────────────────

export const providerApi = {
  getOwn: async (): Promise<CharterProvider> =>
    unwrap(await ApiFetch.get<Enveloped<CharterProvider>>(`${BASE}/me/provider`)),
  updateProfile: async (data: UpdateProviderProfilePayload): Promise<CharterProvider> =>
    unwrap(await ApiFetch.put<Enveloped<CharterProvider>>(`${BASE}/me/provider/profile`, data)),
  updateBrandAssets: async (data: UpdateBrandAssetsPayload): Promise<CharterProvider> =>
    unwrap(await ApiFetch.put<Enveloped<CharterProvider>>(`${BASE}/me/provider/brand-assets`, data)),
  updateGatewayState: async (data: UpdateGatewayStatePayload): Promise<CharterProvider> =>
    unwrap(await ApiFetch.put<Enveloped<CharterProvider>>(`${BASE}/me/provider/gateway-state`, data)),
  updateCancellationPolicy: async (data: CharterCancellationPolicy): Promise<CharterProvider> =>
    unwrap(await ApiFetch.put<Enveloped<CharterProvider>>(`${BASE}/me/provider/cancellation-policy`, data)),
};

// ── Merchant · Availability ──────────────────────────────────────────────────

export const availabilityApi = {
  get: async (): Promise<Availability> =>
    unwrap(await ApiFetch.get<Enveloped<Availability>>(`${BASE}/me/availability`)),
  // charter-api uses PUT (full replace) for availability windows — use legacy_put.
  setWindows: async (data: SetAvailabilityPayload): Promise<Availability> =>
    unwrap(await ApiFetch.legacy_put<Enveloped<Availability>>(`${BASE}/me/availability`, data)),
};

// ── Merchant · Intake form ───────────────────────────────────────────────────

export const intakeApi = {
  getOwn: async (): Promise<IntakeForm> =>
    unwrap(await ApiFetch.get<Enveloped<IntakeForm>>(`${BASE}/me/intake-form`)),
  getEffective: async (): Promise<IntakeForm> =>
    unwrap(await ApiFetch.get<Enveloped<IntakeForm>>(`${BASE}/me/intake-form/effective`)),
  setForm: async (data: SetIntakeFormPayload): Promise<IntakeForm> =>
    unwrap(await ApiFetch.legacy_put<Enveloped<IntakeForm>>(`${BASE}/me/intake-form`, data)),
};

// ── Merchant · Portfolio ─────────────────────────────────────────────────────

export const portfolioApi = {
  list: async (query?: PaginationQuery): Promise<PortfolioWork[]> =>
    unwrap(await ApiFetch.get<Enveloped<PortfolioWork[]>>(`${BASE}/me/portfolio${qs(query)}`)),
  create: async (data: CreatePortfolioWorkPayload): Promise<PortfolioWork> =>
    unwrap(await ApiFetch.post<Enveloped<PortfolioWork>>(`${BASE}/me/portfolio`, data)),
  update: async (id: string, data: UpdatePortfolioWorkPayload): Promise<PortfolioWork> =>
    unwrap(await ApiFetch.put<Enveloped<PortfolioWork>>(`${BASE}/me/portfolio/${id}`, data)),
  remove: async (id: string): Promise<void> => {
    await ApiFetch.del(`${BASE}/me/portfolio/${id}`);
  },
  upload: async (file: File): Promise<{ id: string; cdnUrl: string }> => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      await ApiFetch.post<Enveloped<{ id: string; cdnUrl: string }>>(`${BASE}/me/portfolio/upload`, form, true),
    );
  },
};

// ── Merchant · Waitlist ──────────────────────────────────────────────────────

export const waitlistApi = {
  list: async (query?: PaginationQuery): Promise<WaitlistEntry[]> =>
    unwrap(await ApiFetch.get<Enveloped<WaitlistEntry[]>>(`${BASE}/me/waitlist${qs(query)}`)),
  update: async (id: string, data: UpdateWaitlistEntryPayload): Promise<WaitlistEntry> =>
    unwrap(await ApiFetch.put<Enveloped<WaitlistEntry>>(`${BASE}/me/waitlist/${id}`, data)),
  remove: async (id: string): Promise<void> => {
    await ApiFetch.del(`${BASE}/me/waitlist/${id}`);
  },
};

// ── TCS Admin · cross-provider oversight ─────────────────────────────────────

export const adminApi = {
  listProviders: async (query?: PaginationQuery): Promise<CharterProvider[]> =>
    unwrap(await ApiFetch.get<Enveloped<CharterProvider[]>>(`${BASE}/admin/providers${qs(query)}`)),
  getProvider: async (merchantId: string): Promise<CharterProvider> =>
    unwrap(await ApiFetch.get<Enveloped<CharterProvider>>(`${BASE}/admin/providers/${merchantId}`)),
  listProviderAddons: async (merchantId: string, query?: PaginationQuery): Promise<CharterAddon[]> =>
    unwrap(await ApiFetch.get<Enveloped<CharterAddon[]>>(`${BASE}/admin/providers/${merchantId}/addons${qs(query)}`)),
  createProviderAddon: async (merchantId: string, data: CreateAddonPayload): Promise<CharterAddon> =>
    unwrap(await ApiFetch.post<Enveloped<CharterAddon>>(`${BASE}/admin/providers/${merchantId}/addons`, data)),
  updateProviderAddon: async (merchantId: string, id: string, data: UpdateAddonPayload): Promise<CharterAddon> =>
    unwrap(await ApiFetch.put<Enveloped<CharterAddon>>(`${BASE}/admin/providers/${merchantId}/addons/${id}`, data)),
  removeProviderAddon: async (merchantId: string, id: string): Promise<void> => {
    await ApiFetch.del(`${BASE}/admin/providers/${merchantId}/addons/${id}`);
  },
  listBookings: async (query?: PaginationQuery): Promise<CharterBooking[]> =>
    unwrap(await ApiFetch.get<Enveloped<CharterBooking[]>>(`${BASE}/admin/bookings${qs(query)}`)),
  listOrders: async (query?: PaginationQuery): Promise<CharterOrder[]> =>
    unwrap(await ApiFetch.get<Enveloped<CharterOrder[]>>(`${BASE}/admin/orders${qs(query)}`)),
  listWaitlist: async (query?: PaginationQuery): Promise<WaitlistEntry[]> =>
    unwrap(await ApiFetch.get<Enveloped<WaitlistEntry[]>>(`${BASE}/admin/waitlist${qs(query)}`)),
};
